import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Check, Sparkles, Layers, RefreshCw, Boxes } from "lucide-react";
import { PageHeader, StartCTA } from "@/components/site/primitives";
import { publicCatalog } from "@/lib/api";
import { Seo } from "@/seo/Seo";
import { STATIC_META } from "@/seo/config";

const reveal = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const CATEGORY_LABEL = {
  BUSINESS_FORMATION: "Business Formation & Infrastructure",
  STRATEGY_PLANNING: "Strategy & Planning",
  IMPLEMENTATION_DELIVERY: "Implementation & Delivery",
  FRACTIONAL_ONGOING: "Fractional & Ongoing",
};
const TYPE = {
  CORE: { label: "Core", icon: Boxes, cls: "text-zinc-400 border-[#232323]" },
  ADD_ON: { label: "Add-on", icon: Sparkles, cls: "text-cyan-300 border-cyan-400/40" },
  BUNDLE: { label: "Bundle", icon: Layers, cls: "text-cyan-300 border-cyan-400/40" },
  RECURRING: { label: "Ongoing", icon: RefreshCw, cls: "text-emerald-300 border-emerald-400/30" },
};

const money = (n) => "$" + Number(n || 0).toLocaleString();
function priceLabel(s) {
  const suf = s.price_suffix ? ` ${s.price_suffix}` : "";
  if (s.base_price != null) return money(s.base_price) + suf;
  if (s.minimum_price != null && s.maximum_displayed_price != null) return `${money(s.minimum_price)} – ${money(s.maximum_displayed_price)}`;
  if (s.minimum_price != null) return `From ${money(s.minimum_price)}`;
  return "Custom quote";
}

function ServiceCard({ s }) {
  const t = TYPE[s.service_type] || TYPE.CORE;
  const Icon = t.icon;
  return (
    <motion.div {...reveal} data-testid={`service-card-${s.service_id}`}
      className="rounded-2xl border border-[#161616] bg-[#0a0a0a] p-7 md:p-8 flex flex-col hover:border-cyan-400/25 transition-colors duration-300">
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className="font-heading text-xl md:text-2xl text-white tracking-tight">{s.service_name}</span>
        <span className={`shrink-0 inline-flex items-center gap-1.5 mono-label text-[10px] rounded-full border px-2.5 py-1 ${t.cls}`}>
          <Icon className="h-3 w-3" />{t.label}
        </span>
      </div>
      <div data-testid={`service-price-${s.service_id}`} className="mb-4">
        <span className="font-heading text-2xl md:text-3xl text-cyan-300 tracking-tight">{priceLabel(s)}</span>
        {s.minimum_commitment && <span className="ml-2 text-xs text-zinc-500">· {s.minimum_commitment}</span>}
      </div>
      <p className="text-zinc-400 leading-relaxed text-[15px]">{s.description}</p>
      {s.included_scope?.length > 0 && (
        <ul className="mt-6 pt-5 border-t border-[#161616] grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
          {s.included_scope.slice(0, 6).map((x) => (
            <li key={x} className="flex items-start gap-2.5">
              <Check className="h-4 w-4 mt-0.5 shrink-0 text-zinc-500" />
              <span className="text-sm text-zinc-400">{x}</span>
            </li>
          ))}
        </ul>
      )}
      {(s.government_fees_separate || s.third_party_fees_separate) && (
        <div className="mt-5 mono-label text-[10px] text-zinc-600">
          {s.government_fees_separate ? "Government fees billed separately. " : ""}
          {s.third_party_fees_separate ? "Third-party fees billed separately." : ""}
        </div>
      )}
    </motion.div>
  );
}

export default function Services() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    publicCatalog().then(setData).catch(() => setErr("Could not load services right now."));
  }, []);

  const services = data?.services || [];
  const byCategory = (data?.categories || []).map((cat) => ({
    cat,
    label: CATEGORY_LABEL[cat] || cat,
    items: services.filter((s) => s.category === cat),
  })).filter((g) => g.items.length > 0);

  // Default to the first category so the page shows ONE clean group at a time.
  const active = filter || byCategory[0]?.cat;
  const activeGroup = byCategory.find((g) => g.cat === active);

  return (
    <div>
      <Seo path="/services" title={STATIC_META["/services"].title} description={STATIC_META["/services"].description} />
      <PageHeader
        label="SERVICES & OFFERINGS"
        title={"Everything we can\nbuild and run with you."}
        intro="Pick a category to see exactly what's included and what it costs — no endless scrolling. Every add-on has a clear price so you always know what you're paying for."
      />

      {!data && !err && (
        <div className="flex justify-center py-24" data-testid="services-loading">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
        </div>
      )}
      {err && <div className="text-center text-zinc-500 py-24" data-testid="services-error">{err}</div>}

      {data && byCategory.length > 0 && (
        <>
          {/* Category tabs — only the selected category's offerings show below */}
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex flex-wrap gap-2.5" data-testid="services-filters">
              {byCategory.map((g) => (
                <button
                  key={g.cat}
                  data-testid={`services-filter-${g.cat}`}
                  onClick={() => setFilter(g.cat)}
                  className={`mono-label text-[11px] rounded-full px-4 py-2 border transition-colors duration-200 ${
                    active === g.cat
                      ? "bg-cyan-400 text-black border-cyan-400"
                      : "text-zinc-400 border-[#232323] hover:border-cyan-400/40 hover:text-cyan-300"
                  }`}
                >
                  {g.label}
                  <span className="ml-2 opacity-60">{g.items.length}</span>
                </button>
              ))}
            </div>
          </div>

          {activeGroup && (
            <section key={activeGroup.cat} className="py-12 md:py-16" data-testid={`services-category-${activeGroup.cat}`}>
              <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <motion.h2 key={activeGroup.cat} {...reveal} className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-10">
                  {activeGroup.label}
                </motion.h2>
                <div className="grid md:grid-cols-2 gap-5">
                  {activeGroup.items.map((s) => <ServiceCard key={s.service_id} s={s} />)}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <StartCTA />
    </div>
  );
}
